import React, { useCallback } from 'react';

import { useDispatch, useSelector, useStore } from 'react-redux';

import PropTypes from 'prop-types';

import Button from '@asteria/component-core/button';
import Title, { Text } from '@asteria/component-core/typography';
import Wrapper, {
	Content,
	Footer,
	FooterSection,
	Header,
} from '@asteria/component-core/wrapper';

import List, { ListItem } from '@asteria/component-list';
import Modal from '@asteria/component-modal';

import * as ModalStore from '@asteria/datalayer/stores/modals';
import * as ScenarioStore from '@asteria/datalayer/stores/scenarios';

import { useTranslationFn } from '@asteria/language';
import { cn } from '@asteria/utils-funcs/classes';

const SelectScenarioModal = (props) => {
	const { open, onClose, onSubmit, selectedScenario } = props;

	const translate = useTranslationFn();
	const dispatch = useDispatch();
	const store = useStore();

	const hasHistory = useSelector(ModalStore.selectors.hasHistory);

	const handleClose = useCallback(
		(event) => {
			const hasHistory = ModalStore.selectors.hasHistory(
				store.getState(),
			);

			if (hasHistory) {
				return dispatch(ModalStore.close());
			}

			return onClose?.(event);
		},
		[dispatch, onClose, store],
	);

	const scenarios = useSelector(ScenarioStore.selectors.scenarios).filter(
		(scenario) => scenario.name !== '$base',
	);

	const onCreate = useCallback(() => {
		dispatch(
			ModalStore.open({
				type: ModalStore.MODAL_WINDOWS.ScenarioCreate,
				data: { type: 'FORECAST' },
			}),
		);
	}, [dispatch]);

	return (
		<Modal
			open={open}
			onClose={handleClose}
			size="sm"
			className={cn('asteria--type-scenario', 'asteria--action-reset')}
		>
			<Wrapper>
				<Header onClose={handleClose}>
					{translate('forecaster.modal.select.title')}
				</Header>
				<Content>
					<Text>
						{translate('forecaster.modal.select.description', {
							postfix: {
								hasActiveScenario: !!selectedScenario,
								hasScenarios: scenarios.length > 0,
							},
						})}
					</Text>
					<List>
						{scenarios.map((scenario) => (
							<ListItem key={scenario._id} className="my-2">
								<Title type="title" size="xs" className="grow">
									{scenario.name}
								</Title>
								<Button
									variant="tertiary"
									active={
										scenario._id === selectedScenario?._id
									}
									label={translate(
										'forecaster.modal.select.item.button',
									)}
									onClick={() =>
										onSubmit('scenario:selected', scenario)
									}
								/>
							</ListItem>
						))}
					</List>
				</Content>
				<Footer>
					<FooterSection className="justify-between">
						{hasHistory ? (
							<Button
								variant="tertiary"
								label={translate([
									'button.back',
									'forecaster.modal.select.button.back',
								])}
								onClick={handleClose}
								analyticsKey="forecaster.modal.select.back"
							/>
						) : (
							<Button
								variant="tertiary"
								label={translate([
									'button.cancel',
									'forecaster.modal.select.button.cancel',
								])}
								onClick={handleClose}
								analyticsKey="forecaster.modal.select.cancel"
							/>
						)}
						<Button
							variant="primary"
							label={translate(
								'forecaster.modal.select.item.create',
							)}
							onClick={onCreate}
						/>
					</FooterSection>
				</Footer>
			</Wrapper>
		</Modal>
	);
};

SelectScenarioModal.propTypes = {
	open: PropTypes.bool,
	onAction: PropTypes.func,
	onClose: PropTypes.func,
	data: PropTypes.object,
};

export default SelectScenarioModal;
